import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Components } from "react-markdown";
import { flushSync } from "react-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Send,
  Bot,
  Clock,
  ImagePlus,
  X,
  Loader2,
  Camera,
  Download,
  Film,
  Image as ImageIcon,
} from "lucide-react";
import imageCompression from "browser-image-compression";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessageCircle, ChevronDown, Plus } from "lucide-react";
import Dialog from "./Dialog";
import StreamingMarkdown from "./StreamingMarkdown";
import CopyButton from "./CopyButton";
import SlidingIndicator from "./SlidingIndicator";
import HistorySidebar from "./HistorySidebar";
import BuyCreditsModal from "./BuyCreditsModal";
import BuyChatCreditsModal from "./BuyChatCreditsModal";
import ChatHeader from "./ChatHeader";

import DebateProgress from "./DebateProgress";
import DebateThreadView, { DebateViewSwitch, useDebateView } from "./DebateThreadView";
import AudioDebateView from "./AudioDebateView";
import DebateWizard from "./DebateWizard";
import ModelBrowser from "./ModelBrowser";
import DebateShareMenu from "./DebateShareMenu";
import { freeModelOptions, paidModelOptions } from "../constants/models";
import { useOpenRouterModels } from "../services/openRouterModels";
import { toModelOption } from "../utils/modelCategorizer";
import { hasFreeAccess } from "../utils/freeAccess";
import { expertOptions, MAX_EXPERTS } from "../constants/experts";
import {
  createChatSession,
  saveChatMessages,
  createDebateSession,
  saveDebateSession,
  attachDebateId,
  ChatSession,
  DebateSession,
  StoredMessage,
} from "../firebase/history";
import {
  DebateCredits,
  onCreditsSnapshot,
  hasSufficientChatCredits,
  WeeklyUsage,
  onUsageSnapshot,
  WEEKLY_FREE_DEBATE_LIMIT,
  WEEKLY_AUDIO_DEBATE_LIMIT,
  getGuestUsage,
  incrementGuestUsage,
  GUEST_WEEKLY_FREE_DEBATE_LIMIT,
} from "../firebase/credits";
import { DailyCost, onTodayCostSnapshot } from "../firebase/costs";
import { AudioDebatePlayer } from "../utils/audioPlayer";
import { requestStartDebate, onDebateSnapshot, onSavedDebateSnapshot, loadDebateAudioChunks } from "../firebase/debate";
import { debateResumeState, finishedAudioTurns } from "../utils/debateResume";
import { FeedShare } from "../firebase/feed";
import { DebateLaunch } from "./DebateWizard";
import { AnswerFormat, Verdict, parseAnswerFormat, parseVerdict } from "../utils/verdict";
import { DebateSource, parseSources } from "../utils/sources";
import { useFeedPost } from "../hooks/useFeedPost";
import { FEED_ENABLED } from "../utils/features";
import { useShareLink } from "../hooks/useShareLink";
import { loadDebatePrefs } from "../utils/debatePrefs";
import { startChatStream } from "../firebase/chatStream";
import { GeneratedMediaItem } from "../types/media";

interface Message {
  id: string;
  text: string;
  sender: "user" | "ai";
  timestamp: Date;
  model?: string;
  images?: string[]; // base64 data URIs for user-attached photos
  media?: GeneratedMediaItem[];
  ephemeral?: boolean;
  error?: string;
  retryText?: string;
}

type GenerationMode = "text" | "image" | "video";

const MAX_CHAT_IMAGES = 2;
const IMAGE_URL_RE = /\.(png|jpe?g|webp|gif|bmp|svg)(?:$|[?#])/i;
const VIDEO_URL_RE = /\.(mp4|webm|mov|m4v|avi|mkv)(?:$|[?#])/i;
const MARKDOWN_IMAGE_RE = /!\[[^\]]*\]\((https?:\/\/[^)\s]+)\)/gi;
const URL_RE = /https?:\/\/[^\s<>()]+/gi;

function stripTrailingPunctuation(url: string): string {
  return url.replace(/[),.;!?]+$/, "");
}

function getMediaTypeFromUrl(url: string): "image" | "video" | null {
  if (IMAGE_URL_RE.test(url)) return "image";
  if (VIDEO_URL_RE.test(url)) return "video";
  return null;
}

function extractGeneratedMediaFromText(text: string): GeneratedMediaItem[] {
  const seen = new Set<string>();
  const found: GeneratedMediaItem[] = [];

  let markdownImageMatch: RegExpExecArray | null;
  while ((markdownImageMatch = MARKDOWN_IMAGE_RE.exec(text)) !== null) {
    const url = stripTrailingPunctuation(markdownImageMatch[1]);
    if (!url) continue;
    const key = `image|${url}`;
    if (seen.has(key)) continue;
    found.push({ type: "image", url });
    seen.add(key);
  }

  let urlMatch: RegExpExecArray | null;
  while ((urlMatch = URL_RE.exec(text)) !== null) {
    const url = stripTrailingPunctuation(urlMatch[0]);
    const mediaType = getMediaTypeFromUrl(url);
    if (!mediaType) continue;
    const key = `${mediaType}|${url}`;
    if (seen.has(key)) continue;
    found.push({ type: mediaType, url });
    seen.add(key);
  }

  return found;
}

function normalizeGeneratedMedia(
  media: GeneratedMediaItem[] | undefined,
  text: string,
): GeneratedMediaItem[] {
  const result: GeneratedMediaItem[] = [];
  const seen = new Set<string>();
  const merged = [...(media || []), ...extractGeneratedMediaFromText(text)];

  for (const item of merged) {
    if (!item || typeof item.url !== "string") continue;
    const url = stripTrailingPunctuation(item.url.trim());
    if (
      !url.startsWith("http://") &&
      !url.startsWith("https://") &&
      !url.startsWith("data:")
    )
      continue;
    const type =
      item.type ||
      (url.startsWith("data:") ? "image" : getMediaTypeFromUrl(url));
    if (type !== "image" && type !== "video") continue;
    const key = `${type}|${url.substring(0, 200)}`;
    if (seen.has(key)) continue;
    result.push({ ...item, type, url });
    seen.add(key);
  }

  return result;
}

function inferDownloadFileName(item: GeneratedMediaItem): string {
  if (item.fileName && item.fileName.trim()) return item.fileName.trim();
  try {
    const parsed = new URL(item.url);
    const pathPart = parsed.pathname.split("/").pop() || "";
    const cleaned = decodeURIComponent(pathPart).trim();
    if (cleaned) return cleaned;
  } catch {
    // Fall through to extension-based fallback.
  }

  const ext = item.type === "video" ? "mp4" : "png";
  return `generated-${item.type}-${Date.now()}.${ext}`;
}

function CodeBlock({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) {
  const ref = useRef<HTMLPreElement>(null);
  return (
    <div className="code-block group/code">
      <pre ref={ref} {...props}>{children}</pre>
      <CopyButton
        className="code-copy"
        getText={() => ref.current?.innerText.replace(/\n$/, "") ?? ""}
      />
    </div>
  );
}

const markdownComponents: Components = {
  pre: ({ node, ...props }) => <CodeBlock {...props} />,
  table: ({ node, ...props }) => (
    <div className="my-3 overflow-x-auto rounded-xl border border-line bg-subtle/60 shadow-sm">
      <table
        className="w-full min-w-[560px] border-collapse text-sm"
        {...props}
      />
    </div>
  ),
  thead: ({ node, ...props }) => (
    <thead className="bg-pressed/80" {...props} />
  ),
  tbody: ({ node, ...props }) => <tbody className="bg-surface" {...props} />,
  tr: ({ node, ...props }) => (
    <tr
      className="border-b border-line last:border-b-0 odd:bg-surface even:bg-subtle/65 hover:bg-accent-soft/60 transition-colors"
      {...props}
    />
  ),
  th: ({ node, ...props }) => (
    <th
      className="border-b border-control px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-content"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td
      className="px-3 py-2.5 align-top text-label leading-relaxed text-content"
      {...props}
    />
  ),
};

const Chat: React.FC = () => {
  const { currentUser, logout, isGuest, exitGuestMode } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [selectedModel, setSelectedModel] = useState(currentUser ? "openrouter/auto" : freeModelOptions[0].value);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const councilMode = searchParams.get("mode") !== "chat";
  const setCouncilMode = (mode: boolean | ((previous: boolean) => boolean)) => {
    const next = typeof mode === "function" ? mode(councilMode) : mode;
    setSearchParams(next ? {} : { mode: "chat" });
  };
  const [showModelBrowser, setShowModelBrowser] = useState(false);
  const [generationMode, setGenerationMode] = useState<GenerationMode>("text");
  const [savedPrefs] = useState(loadDebatePrefs);
  const [plan, setPlan] = useState<"free" | "plus" | "pro">(savedPrefs.plan);
  const rounds = 1; // always 1 round
  const [concise, setConcise] = useState(savedPrefs.concise);
  const [audioMode, setAudioMode] = useState(savedPrefs.audioMode);
  const [debateMode, setDebateMode] = useState<"single" | "multi">(savedPrefs.debateMode);
  // Audio debate state
  const [currentSpeaker, setCurrentSpeaker] = useState<string | null>(null);
  const [currentSpeakerLabel, setCurrentSpeakerLabel] = useState<string | null>(
    null,
  );
  const [currentPhase, setCurrentPhase] = useState<string | null>(null);
  const [completedAudioTurns, setCompletedAudioTurns] = useState<string[]>([]);
  const [audioPlaybackState, setAudioPlaybackState] = useState<
    "playing" | "paused" | "idle"
  >("idle");
  const audioPlayerRef = useRef<AudioDebatePlayer | null>(null);
  // Set while a debate from history is open. A live debate follows the audio toggle.
  const [reopenedFormat, setReopenedFormat] = useState<"checking" | "audio" | "text" | null>(null);
  const showAudioDebate = reopenedFormat ? reopenedFormat === "audio" : audioMode;
  const savedAudioRef = useRef<{ debateId: string; turns: string[] } | null>(null);
  const [audioReplayError, setAudioReplayError] = useState<string | null>(null);

  // Stable speaker-change callback for AudioDebatePlayer
  const handleSpeakerChange = useCallback(
    (speaker: string | null, label: string | null, phase: string | null) => {
      setCurrentSpeaker(speaker);
      setCurrentSpeakerLabel(label);
      setCurrentPhase(phase);
    },
    [],
  );

  // ── Audio playback controls ──────────────────────────────────────────────
  const handleAudioPause = useCallback(() => {
    audioPlayerRef.current?.pause();
  }, []);

  const handleAudioResume = useCallback(() => {
    audioPlayerRef.current?.resume();
  }, []);

  const createAudioPlayer = useCallback(() => {
    const player = new AudioDebatePlayer(handleSpeakerChange);
    player.init(); // play() inside the user gesture — grants autoplay permission
    player.setOnPlaybackStateChange(setAudioPlaybackState);
    player.setOnTurnComplete((turn) => {
      setCompletedAudioTurns((prev) => [...prev, `${turn.speaker}-${turn.phase}`]);
    });
    return player;
  }, [handleSpeakerChange]);

  // A debate reopened from history has no audio in memory until replay is tapped.
  const handleAudioReplay = useCallback(() => {
    setAudioReplayError(null);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.replay(() => setCompletedAudioTurns([]));
      return;
    }
    const saved = savedAudioRef.current;
    if (!saved) return;
    // Created before the download, while still inside the tap, for iOS autoplay.
    const player = createAudioPlayer();
    audioPlayerRef.current = player;
    setCompletedAudioTurns([]);
    setAudioPlaybackState("playing");
    loadDebateAudioChunks(saved.debateId)
      .then((chunks) => {
        if (audioPlayerRef.current !== player) return;
        if (chunks.length === 0) throw new Error("no saved audio");
        for (const chunk of chunks) {
          player.enqueueChunk(
            chunk.speaker,
            chunk.speakerLabel || chunk.speaker,
            chunk.phase,
            chunk.audioChunk,
            chunk.done === true,
          );
        }
      })
      .catch((error) => {
        if (audioPlayerRef.current !== player) return;
        console.error("Error loading saved debate audio:", error);
        player.stop();
        audioPlayerRef.current = null;
        setCompletedAudioTurns(saved.turns);
        setAudioReplayError("This debate's audio couldn't be loaded.");
      });
  }, [createAudioPlayer]);

  const [selectedExperts, setSelectedExperts] = useState<string[]>(() => {
    const known = new Set(expertOptions.map((o) => o.value));
    return savedPrefs.experts.filter((e) => known.has(e));
  });
  const [debateTopic, setDebateTopic] = useState<string | null>(null);
  // ── Image state for plain chat mode ─────────────────────────────────────
  const [chatImages, setChatImages] = useState<string[]>([]);
  const [compressingChatImages, setCompressingChatImages] = useState(false);
  const [downloadingMediaUrl, setDownloadingMediaUrl] = useState<string | null>(
    null,
  );
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState<string | null>(
    null,
  );
  const [failedMediaUrls, setFailedMediaUrls] = useState<
    Record<string, boolean>
  >({});
  const chatFileInputRef = useRef<HTMLInputElement>(null);
  // Images passed from the DebateWizard for the current debate
  const debateImagesRef = useRef<string[]>([]);

  const handleChatImageSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await attachChatImages(Array.from(files));
  };

  const handleChatPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (generationMode !== "text" || isLoading) return;
    const files = Array.from(e.clipboardData.items)
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    if (files.length === 0) return;
    e.preventDefault();
    void attachChatImages(files);
  };

  const attachChatImages = async (files: File[]) => {
    const filesToProcess = files.slice(
      0,
      MAX_CHAT_IMAGES - chatImages.length,
    );
    if (filesToProcess.length === 0) return;
    setCompressingChatImages(true);
    try {
      const dataUris = await Promise.all(
        filesToProcess.map(async (file) => {
          const compressed = await imageCompression(file, {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
            fileType: "image/jpeg",
          });
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(compressed);
          });
        }),
      );
      setChatImages((prev) => [...prev, ...dataUris].slice(0, MAX_CHAT_IMAGES));
    } catch (err) {
      console.error("Image compression failed:", err);
    } finally {
      setCompressingChatImages(false);
      if (chatFileInputRef.current) chatFileInputRef.current.value = "";
    }
  };

  const removeChatImage = (idx: number) => {
    setChatImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const markMediaLoadError = (url: string) => {
    setFailedMediaUrls((prev) => ({ ...prev, [url]: true }));
  };

  const handleMediaDownload = async (item: GeneratedMediaItem) => {
    const fileName = inferDownloadFileName(item);
    setDownloadingMediaUrl(item.url);
    try {
      // Use a GET URL so Safari treats it as a native navigation download
      // and shows it in the download bar.
      const proxyBase = "https://downloadmedia-mt62q3wpaa-uc.a.run.app";
      const params = new URLSearchParams({
        url: item.url,
        name: fileName,
      });
      const downloadUrl = `${proxyBase}?${params.toString()}`;

      // Open the proxy URL directly — the Content-Disposition: attachment
      // header from the backend triggers a real browser download.
      window.location.href = downloadUrl;
    } catch (err) {
      console.warn("Media download failed, opening in new tab", err);
      window.open(item.url, "_blank", "noopener,noreferrer");
    } finally {
      // Small delay so the spinner shows briefly
      setTimeout(() => setDownloadingMediaUrl(null), 1500);
    }
  };

  const [expandedExperts, setExpandedExperts] = useState<
    Record<string, boolean>
  >({});
  const [councilProgress, setCouncilProgress] = useState<any[]>([]);
  const [debateView, setDebateView] = useDebateView();
  const [chaosProgress, setChaosProgress] = useState<any[]>([]);
  const [councilFinal, setCouncilFinal] = useState<string | null>(null);
  const [facilitatorDone, setFacilitatorDone] = useState(false);
  // Tracks whether the user has clicked "Start Debate" in the wizard
  const [debateStarted, setDebateStarted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Always-current mirror of the messages state — safe to read inside async closures
  const allMessagesRef = useRef<Message[]>([]);
  // Firestore debate subscription cleanup
  const debateUnsubRef = useRef<(() => void) | null>(null);
  // Firestore chat stream subscription cleanup
  const chatStreamUnsubRef = useRef<(() => void) | null>(null);
  const activeDebateId = useRef<string | null>(null);
  // The finished debate on screen, as state so the feed button can follow it.
  const [shownDebateId, setShownDebateId] = useState<string | null>(null);
  const feedShareRef = useRef<FeedShare | null>(null);
  // The answer format and category worked out during setup, sent with the next debate.
  const debateProfileRef = useRef<{ answerFormat?: AnswerFormat; category?: string }>({});
  const [councilVerdict, setCouncilVerdict] = useState<Verdict | null>(null);
  const [debateAnswerFormat, setDebateAnswerFormat] = useState<AnswerFormat | null>(null);
  // Mirrors for the history save, which runs inside a snapshot callback.
  const councilVerdictRef = useRef<Verdict | null>(null);
  const debateAnswerFormatRef = useRef<AnswerFormat | null>(null);
  const showVerdict = (verdict: Verdict | null, format: AnswerFormat | null) => {
    councilVerdictRef.current = verdict;
    debateAnswerFormatRef.current = format;
    setCouncilVerdict(verdict);
    setDebateAnswerFormat(format);
  };
  const [debateSources, setDebateSources] = useState<DebateSource[]>([]);
  const debateSourcesRef = useRef<DebateSource[]>([]);
  const showSources = (sources: DebateSource[]) => {
    // Every snapshot parses a fresh copy; only a real change re-renders.
    if (JSON.stringify(sources) === JSON.stringify(debateSourcesRef.current)) return;
    debateSourcesRef.current = sources;
    setDebateSources(sources);
  };
  const feedPost = useFeedPost(shownDebateId, !!currentUser && FEED_ENABLED);
  const shareLink = useShareLink(shownDebateId, !!currentUser);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  const isLoggedIn = !!currentUser;

  // ── Debate credits state ──────────────────────────────────────────────────
  const [credits, setCredits] = useState<DebateCredits>({
    plusCredits: 0,
    proCredits: 0,
    chatCredits: 0,
    initialized: false,
  });
  // 'chat' = open chat credits modal, 'debate' = open debate credits modal, null = closed
  const [buyCreditsContext, setBuyCreditsContext] = useState<
    "chat" | "debate" | null
  >(null);
  const [showHistory, setShowHistory] = useState(false);

  // Weekly usage tracking (for free & audio debate limits)
  const [usage, setUsage] = useState<WeeklyUsage>({
    weekStart: 0,
    freeDebates: 0,
    audioDebates: 0,
  });
  const [showUsageLimitModal, setShowUsageLimitModal] = useState<string | null>(
    null,
  ); // "free" | "audio" | null
  // Firestore session IDs for the active session (null = not yet saved)
  const activeChatSessionId = useRef<string | null>(null);
  const activeDebateSessionId = useRef<string | null>(null);

  // Subscribe to real-time credit updates (only for logged-in users)
  useEffect(() => {
    if (!currentUser) {
      // Guest: zero credits, no subscription
      setCredits({
        plusCredits: 0,
        proCredits: 0,
        chatCredits: 0,
        initialized: false,
      });
      return;
    }
    const unsub = onCreditsSnapshot(currentUser.uid, setCredits);
    return () => unsub();
  }, [currentUser]);

  // Subscribe to weekly usage (only for logged-in users; guests use localStorage)
  useEffect(() => {
    if (!currentUser) {
      // Load guest usage from localStorage so the header can show remaining count
      setUsage(getGuestUsage());
      return;
    }
    const unsub = onUsageSnapshot(currentUser.uid, setUsage);
    return () => unsub();
  }, [currentUser]);

  // ── Today's API cost tracking ─────────────────────────────────────────────
  const [todayCost, setTodayCost] = useState<DailyCost>({
    chat: 0,
    debate: 0,
    total: 0,
  });
  useEffect(() => {
    if (!currentUser) {
      setTodayCost({ chat: 0, debate: 0, total: 0 });
      return;
    }
    const unsub = onTodayCostSnapshot(currentUser.uid, setTodayCost);
    return () => unsub();
  }, [currentUser]);

  // If the user is a guest and not logged in, redirect to login on first render
  useEffect(() => {
    if (!currentUser && !isGuest) {
      navigate("/login");
    }
  }, [currentUser, isGuest, navigate]);

  // Privileged users can choose Plus/Pro debate tiers; everyone else is locked to Free
  const isPrivileged = isLoggedIn && hasFreeAccess(currentUser?.email);

  // ── Dynamic model catalog from OpenRouter API ────────────────────────────
  const {
    models: allOpenRouterModels,
    loading: modelsLoading,
    error: modelsError,
    refresh: refreshModels,
  } = useOpenRouterModels();

  // Pass all models to the browser — category chips let users filter
  const availableModels = allOpenRouterModels;

  const generationFilteredModels = useMemo(() => {
    if (generationMode === "image") {
      return availableModels.filter((m) =>
        m.architecture?.output_modalities?.includes("image"),
      );
    }
    if (generationMode === "video") {
      return availableModels.filter((m) =>
        m.architecture?.output_modalities?.includes("video"),
      );
    }
    return availableModels;
  }, [availableModels, generationMode]);

  // Backward-compat ModelOption[] for ChatHeader badge display
  const modelOptions =
    availableModels.length > 0
      ? availableModels.map(toModelOption)
      : isLoggedIn
        ? paidModelOptions
        : freeModelOptions;

  // Keep selected model compatible with the chosen output mode when possible.
  useEffect(() => {
    if (councilMode || generationMode === "text") return;
    if (generationFilteredModels.length === 0) return;
    const supported = generationFilteredModels.some(
      (m) => m.id === selectedModel,
    );
    if (!supported) {
      setSelectedModel(generationFilteredModels[0].id);
    }
  }, [councilMode, generationMode, generationFilteredModels, selectedModel]);

  // If the currently-selected model isn't in the available list, reset to the top option
  useEffect(() => {
    // openrouter/auto is a special routing model; it won't appear in the list
    if (selectedModel === "openrouter/auto") return;
    if (
      modelOptions.length > 0 &&
      !modelOptions.find((m) => m.value === selectedModel)
    ) {
      setSelectedModel(!isLoggedIn ? modelOptions.find(m => m.value.endsWith(":free"))?.value ?? modelOptions[0].value : modelOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, modelOptions.length]);

  // Follow new output only while the reader is already at the bottom; someone
  // who scrolled up to reread keeps their place and gets a jump control instead.
  const nearBottomRef = useRef(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    const added = messages.length !== prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;
    if (messages.length === 0) return;
    if (!nearBottomRef.current) {
      if (added && messages.length > 0) setShowJumpToLatest(true);
      return;
    }
    // Smooth-scrolling on every streamed chunk stacks animations and judders.
    scrollToBottom(added ? "smooth" : "auto");
  }, [messages]);

  // Revealed text grows between message updates, so keep the reader pinned
  // to the newest line by watching the DOM rather than the message state.
  const chatScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = chatScrollRef.current;
    if (councilMode || !el || typeof MutationObserver === "undefined") return;
    let frame = 0;
    const observer = new MutationObserver(() => {
      if (frame || !nearBottomRef.current || allMessagesRef.current.length === 0) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (nearBottomRef.current) el.scrollTop = el.scrollHeight;
      });
    });
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [councilMode]);

  const handleMessagesScroll = (e: React.UIEvent<HTMLElement>) => {
    const el = e.target as HTMLElement;
    if (el.scrollHeight <= el.clientHeight) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
    nearBottomRef.current = nearBottom;
    if (nearBottom) setShowJumpToLatest(false);
  };

  useEffect(() => {
    if (inputText === "" && inputRef.current) inputRef.current.style.height = "";
  }, [inputText]);

  // Keep the ref in sync so async closures always see the latest messages.
  // IMPORTANT: useEffect runs after render, but we also need the ref updated
  // synchronously inside setMessages calls (see handleSubmit).
  useEffect(() => {
    allMessagesRef.current = messages;
  }, [messages]);

  // Clean up Firestore subscriptions on unmount
  useEffect(() => {
    return () => {
      if (debateUnsubRef.current) {
        debateUnsubRef.current();
        debateUnsubRef.current = null;
      }
      if (chatStreamUnsubRef.current) {
        chatStreamUnsubRef.current();
        chatStreamUnsubRef.current = null;
      }
    };
  }, []);

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    messagesEndRef.current?.scrollIntoView({ behavior: reduceMotion ? "auto" : behavior, block: "end" });
  };

  const leaveReopenedDebate = () => {
    setReopenedFormat(null);
    savedAudioRef.current = null;
    setAudioReplayError(null);
  };

  const startNewDebate = () => {
    setDebateTopic(null);
    setCouncilProgress([]);
    setChaosProgress([]);
    setCouncilFinal(null);
    showVerdict(null, null);
    showSources([]);
    setFacilitatorDone(false);
    setDebateStarted(false);
    setDebateError(null);
    leaveReopenedDebate();
    setIsLoading(false);
    setMessages([]);
    allMessagesRef.current = [];
    setExpandedExperts({});
    setInputText("");
    activeChatSessionId.current = null;
    activeDebateSessionId.current = null;
    // Clean up Firestore subscriptions
    if (debateUnsubRef.current) {
      debateUnsubRef.current();
      debateUnsubRef.current = null;
    }
    if (chatStreamUnsubRef.current) {
      chatStreamUnsubRef.current();
      chatStreamUnsubRef.current = null;
    }
    activeDebateId.current = null;
    setShownDebateId(null);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.stop();
      audioPlayerRef.current = null;
    }
    setCurrentSpeaker(null);
    setCurrentSpeakerLabel(null);
    setCurrentPhase(null);
    setCompletedAudioTurns([]);
    // Clear images
    setChatImages([]);
    debateImagesRef.current = [];
  };

  const jumpToLatest = () => {
    nearBottomRef.current = true;
    setShowJumpToLatest(false);
    scrollToBottom("smooth");
  };

  const markLastAiMessageFailed = (error: string, retryText: string) => {
    setMessages((prev) => {
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      if (lastIndex >= 0 && updated[lastIndex].sender === "ai") {
        updated[lastIndex] = { ...updated[lastIndex], error, retryText };
      }
      allMessagesRef.current = updated;
      return updated;
    });
  };

  const [debateError, setDebateError] = useState<string | null>(null);

  const handleRetryMessage = (aiMessageId: string) => {
    const msgs = allMessagesRef.current;
    const idx = msgs.findIndex((m) => m.id === aiMessageId);
    const failed = msgs[idx];
    if (idx < 1 || !failed?.retryText || isLoading) return;
    const userTurn = msgs[idx - 1];
    const trimmed = msgs.slice(0, idx - 1);
    allMessagesRef.current = trimmed;
    setMessages(trimmed);
    handleSubmitWithQuestion(
      failed.retryText,
      { preventDefault: () => {} } as React.FormEvent,
      userTurn?.images ?? [],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || isLoading) return;
    setShowModelBrowser(false);
    // For chat mode, delegate to the shared handler
    await handleSubmitWithQuestion(inputText, {
      preventDefault: () => {},
    } as React.FormEvent);
  };

  const handleLogout = async () => {
    try {
      if (currentUser) {
        await logout();
      }
      exitGuestMode();
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  // Redirect guests to login to sign in (e.g. when they try to open history)
  const handleSignIn = () => {
    exitGuestMode();
    navigate("/login");
  };

  const handleNewChat = () => {
    setMessages([]);
    setDebateError(null);
    leaveReopenedDebate();
    setIsLoading(false);
    setShowJumpToLatest(false);
    nearBottomRef.current = true;
    allMessagesRef.current = [];
    setInputText("");
    activeChatSessionId.current = null;
    activeDebateSessionId.current = null;
    // Clean up Firestore subscriptions
    if (debateUnsubRef.current) {
      debateUnsubRef.current();
      debateUnsubRef.current = null;
    }
    if (chatStreamUnsubRef.current) {
      chatStreamUnsubRef.current();
      chatStreamUnsubRef.current = null;
    }
    activeDebateId.current = null;
    setShownDebateId(null);
    // Reset debate wizard state
    setDebateStarted(false);
    setDebateTopic(null);
    setCouncilProgress([]);
    setChaosProgress([]);
    setCouncilFinal(null);
    showVerdict(null, null);
    showSources([]);
    setFacilitatorDone(false);
    setExpandedExperts({});
    // Clean up audio player
    if (audioPlayerRef.current) {
      audioPlayerRef.current.stop();
      audioPlayerRef.current = null;
    }
    setCurrentSpeaker(null);
    setCurrentSpeakerLabel(null);
    setCurrentPhase(null);
    setCompletedAudioTurns([]);
    setAudioPlaybackState("idle");
    // Clear images
    setChatImages([]);
    setFailedMediaUrls({});
    debateImagesRef.current = [];
    // Reset model picker
    setShowModelBrowser(false);
  };

  const handleExpertToggle = (expert: string) => {
    setSelectedExperts((prev) => {
      if (prev.includes(expert)) return prev.filter((e) => e !== expert);
      if (prev.length >= MAX_EXPERTS) return prev; // cap reached
      return [...prev, expert];
    });
  };

  // ── Called from DebateWizard step 3 "Start Debate" button ─────────────────
  const handleStartDebate = (question: string, images: string[], launch: DebateLaunch = {}) => {
    const experts = launch.experts;
    debateImagesRef.current = images;
    feedShareRef.current = launch.share ?? null;
    debateProfileRef.current = { answerFormat: launch.answerFormat, category: launch.category };
    setShownDebateId(null);
    leaveReopenedDebate();

    // ── Audio player init — MUST happen synchronously inside the user tap ──
    // iOS WebKit (used by ALL iOS browsers including Chrome) only grants
    // autoplay permission to play() calls within the *synchronous* call-stack
    // of a user gesture. The setTimeout below breaks that chain, so we
    // create & init the player here, before the timeout.
    if (audioMode && councilMode && debateMode === "multi") {
      setCurrentSpeaker(null);
      setCurrentSpeakerLabel(null);
      setCurrentPhase(null);
      setCompletedAudioTurns([]);
      if (audioPlayerRef.current) {
        audioPlayerRef.current.reset();
      }
      audioPlayerRef.current = createAudioPlayer();
    }

    // The setup screen hands its topic and panel to the progress view as
    // shared elements; the request is sent at the same time, never delayed.
    const reveal = () => {
      flushSync(() => {
        setInputText(question);
        setDebateTopic(question);
        setDebateStarted(true);
      });
    };
    const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (doc.startViewTransition && !reduceMotion) doc.startViewTransition(reveal);
    else reveal();

    // Build a synthetic form event and fire handleSubmit
    // We set inputText synchronously above, but handleSubmit reads it from state,
    // so we call it after a microtask to let React flush the state update.
    setTimeout(() => {
      // handleSubmit expects inputText to be set; we also set it directly
      const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
      handleSubmitWithQuestion(question, fakeEvent, undefined, experts);
    }, 0);
  };

  // Variant of handleSubmit that takes the question directly (avoids state timing)
  const handleSubmitWithQuestion = async (
    userMessage: string,
    e: React.FormEvent,
    imagesOverride?: string[],
    expertsOverride?: string[],
  ) => {
    e.preventDefault();
    if (!userMessage.trim() || isLoading) return;

    // The wizard passes freshly picked experts because state set in the same
    // tick is not visible to this closure yet.
    const debateExperts = expertsOverride ?? selectedExperts;

    // Hard stop — must have at least 2 experts (multi-expert only)
    if (councilMode && debateMode === "multi" && debateExperts.length < 2)
      return;

    // ── Credit check for paid debate tiers ──────────────────────────────────
    if (
      councilMode &&
      (plan === "plus" || plan === "pro") &&
      !isPrivileged
    ) {
      if (!currentUser) {
        setBuyCreditsContext("debate");
        return;
      }
      const hasCredits =
        plan === "plus" ? credits.plusCredits > 0 : credits.proCredits > 0;
      if (!hasCredits) {
        setBuyCreditsContext("debate");
        return;
      }
    }

    // ── Chat credit check for paid models (plain chat mode) ──────────────────
    if (
      !councilMode &&
      !selectedModel.endsWith(":free") &&
      !isPrivileged
    ) {
      if (!currentUser) {
        setBuyCreditsContext("chat");
        return;
      }
      if (!hasSufficientChatCredits(credits)) {
        setBuyCreditsContext("chat");
        return;
      }
    }

    // ── Generation capability check (plain chat mode) ──────────────────────
    if (!councilMode && generationMode !== "text") {
      const selected = modelOptions.find((m) => m.value === selectedModel);
      const supportsRequestedGeneration =
        generationMode === "image"
          ? !!selected?.imageGeneration
          : !!selected?.videoGeneration;
      if (!supportsRequestedGeneration) {
        setShowModelBrowser(true);
        return;
      }
    }

    // ── Weekly usage limits (signed-in users on free tier) ───────────────────
    // startDebate counts usage; this only avoids a request that would fail.
    if (currentUser && councilMode && plan === "free" && !isPrivileged) {
      if (
        audioMode &&
        debateMode === "multi" &&
        usage.audioDebates >= WEEKLY_AUDIO_DEBATE_LIMIT
      ) {
        setShowUsageLimitModal("audio");
        return;
      }
      if (usage.freeDebates >= WEEKLY_FREE_DEBATE_LIMIT) {
        setShowUsageLimitModal("free");
        return;
      }
    }

    // ── Guest weekly usage limit (localStorage-based) ───────────────────────
    if (!currentUser && councilMode) {
      const ok = incrementGuestUsage("freeDebates");
      if (!ok) {
        setShowUsageLimitModal("guest");
        return;
      }
    }

    setIsLoading(true);
    setDebateError(null);
    if (!imagesOverride) setInputText("");
    nearBottomRef.current = true;
    setShowJumpToLatest(false);
    // Clear chat images after sending (plain chat mode)
    if (!councilMode && !imagesOverride) setChatImages([]);

    // ── Create a new Firestore session stub up-front (only for logged-in users) ──
    if (currentUser) {
      if (!councilMode) {
        if (generationMode === "text" && !activeChatSessionId.current) {
          const ref = await createChatSession(
            currentUser.uid,
            userMessage,
            selectedModel,
          );
          activeChatSessionId.current = ref.id;
        }
      } else {
        const ref = await createDebateSession(
          currentUser.uid,
          userMessage,
          debateExperts,
          rounds,
          plan,
          selectedModel,
          debateMode,
        );
        activeDebateSessionId.current = ref.id;
      }
    }

    if (councilMode) {
      setDebateTopic(userMessage);
      setExpandedExperts({});
    }

    const priorMessages = allMessagesRef.current.slice();

    // Current images — from chat mode picker or debate wizard
    const currentImages = councilMode
      ? debateImagesRef.current
      : imagesOverride ?? chatImages;

    // Check if multiple models were used in this conversation (chat mode only)
    const hasModelSwitches =
      !councilMode &&
      priorMessages.some(
        (m) => m.sender === "ai" && m.model && m.model !== selectedModel,
      );

    const contextWindow = [
      ...priorMessages,
      {
        id: Date.now().toString(),
        text: userMessage,
        sender: "user" as const,
        timestamp: new Date(),
        images: currentImages.length > 0 ? currentImages : undefined,
      } as Message,
    ]
      .filter((m) => m.sender === "user" || m.sender === "ai")
      .filter((m) => m.text.trim() !== "")
      .slice(-10);

    const messagesForContext = contextWindow.map((m) => {
      const role = m.sender === "user" ? "user" : "assistant";
      // Build multimodal content array for user messages with images
      if (role === "user" && m.images && m.images.length > 0) {
        const content: Array<{
          type: string;
          text?: string;
          image_url?: { url: string; detail?: string };
        }> = [
          { type: "text", text: m.text },
          ...m.images.map((dataUri) => ({
            type: "image_url" as const,
            image_url: { url: dataUri, detail: "auto" as const },
          })),
        ];
        return { role, content };
      }
      return { role, content: m.text };
    });

    // ── System prompt for model-switch context (chat mode only) ─────────
    if (hasModelSwitches) {
      const assistantModelMap = contextWindow
        .filter((m) => m.sender === "ai" && m.model)
        .map((m, idx) => `${idx + 1}. ${m.model}`)
        .join("\n");

      messagesForContext.unshift({
        role: "system",
        content:
          `You are now ${selectedModel}. The user switched models during this conversation. ` +
          `Prior assistant replies were produced by these models:\n${assistantModelMap || "(none)"}\n` +
          `Continue the conversation naturally. Do not mention the model switch unless the user asks. if user asks you to compare, do so.`,
      });
    }

    // Always instruct the model to use Markdown, never raw HTML
    messagesForContext.unshift({
      role: "system",
      content:
        "Always format your responses using proper Markdown. " +
        "Use Markdown syntax for headings, lists, tables, code blocks, bold, and italic text. " +
        "Never use raw HTML tags such as <br>, <p>, <b>, <i>, or <table> in your responses.",
    });

    setMessages((prev) => {
      const isEphemeralTurn = !councilMode && generationMode !== "text";
      const next = [
        ...prev,
        {
          id: Date.now().toString(),
          text: userMessage,
          sender: "user" as const,
          timestamp: new Date(),
          images: currentImages.length > 0 ? currentImages : undefined,
          ephemeral: isEphemeralTurn,
        } as Message,
        ...(!councilMode
          ? [
              {
                id: (Date.now() + 1).toString(),
                text: "",
                sender: "ai" as const,
                timestamp: new Date(),
                ephemeral: isEphemeralTurn,
              } as Message,
            ]
          : []),
      ];
      allMessagesRef.current = next;
      return next;
    });

    if (councilMode) {
      setCouncilProgress([]);
      setChaosProgress([]);
      setCouncilFinal(null);
      showVerdict(null, null);
      showSources([]);
      setFacilitatorDone(false);

      if (audioMode) {
        // If the audio player was already initialized in handleStartDebate
        // (synchronously inside the user gesture — required for iOS autoplay),
        // skip re-creating it. Re-initializing here (inside a setTimeout /
        // async callback) would lose the iOS autoplay grant.
        if (!audioPlayerRef.current) {
          setCurrentSpeaker(null);
          setCurrentSpeakerLabel(null);
          setCurrentPhase(null);
          setCompletedAudioTurns([]);
          audioPlayerRef.current = createAudioPlayer();
        }
      }
    }

    if (councilMode) {
      // ── Firestore-backed debate flow ────────────────────────────────────
      // The backend orchestrates the debate and writes progress to Firestore.
      // We subscribe here for real-time updates (typewriter, audio, etc.).
      try {
        // Clean up any prior subscription
        if (debateUnsubRef.current) {
          debateUnsubRef.current();
          debateUnsubRef.current = null;
        }

        const debateId = await requestStartDebate({
          question: userMessage.trim(),
          experts: debateExperts,
          plan,
          concise: debateMode === "single" ? false : concise,
          audio: debateMode === "single" ? false : audioMode,
          uid: currentUser?.uid || null,
          email: currentUser?.email || null,
          images:
            debateImagesRef.current.length > 0
              ? debateImagesRef.current
              : undefined,
          debateMode,
          ...(debateMode === "multi" ? debateProfileRef.current : {}),
        });
        activeDebateId.current = debateId;
        setShownDebateId(debateId);
        if (currentUser && activeDebateSessionId.current) {
          attachDebateId(currentUser.uid, activeDebateSessionId.current, debateId).catch(console.error);
        }

        // Subscribe to Firestore debate doc for real-time updates
        const unsub = onDebateSnapshot(debateId, {
          onSteps: (steps) => {
            setCouncilProgress(steps);
          },
          onChaos: (chaosSteps) => {
            setChaosProgress(chaosSteps);
          },
          onFinal: (finalAggregate) => {
            setCouncilFinal(finalAggregate);
          },
          onVerdict: (verdict, answerFormat) => {
            // Every snapshot parses a fresh copy; only a real change re-renders.
            const changed =
              JSON.stringify(verdict) !== JSON.stringify(councilVerdictRef.current) ||
              JSON.stringify(answerFormat) !== JSON.stringify(debateAnswerFormatRef.current);
            if (changed) showVerdict(verdict, answerFormat);
          },
          onSources: showSources,
          onFacilitatorDone: (done) => {
            if (done) {
              setFacilitatorDone(true);
              setIsLoading(false);

              const share = feedShareRef.current;
              feedShareRef.current = null;
              if (share && currentUser) feedPost.publish({ anonymous: share.anonymous, debateId });

              // Save to debate session history (for logged-in users)
              if (activeDebateSessionId.current && currentUser) {
                setCouncilProgress((cp) => {
                  setChaosProgress((chp) => {
                    setCouncilFinal((cf) => {
                      saveDebateSession(
                        currentUser.uid,
                        activeDebateSessionId.current!,
                        cp,
                        chp,
                        cf,
                        {
                          verdict: councilVerdictRef.current,
                          answerFormat: debateAnswerFormatRef.current,
                          sources: debateSourcesRef.current,
                        },
                      ).catch(console.error);
                      return cf;
                    });
                    return chp;
                  });
                  return cp;
                });
              }
            }
          },
          onAudioChunk: (newChunks) => {
            if (audioPlayerRef.current) {
              for (const chunk of newChunks) {
                audioPlayerRef.current.enqueueChunk(
                  chunk.speaker,
                  chunk.speakerLabel || chunk.speaker,
                  chunk.phase,
                  chunk.audioChunk,
                  chunk.done === true,
                );
              }
            }
          },
          onStatus: (status) => {
            if (status === "error") {
              setIsLoading(false);
              setDebateError("The council stopped before finishing.");
            }
          },
          onError: (error) => {
            console.error("Debate error from backend:", error);
            setIsLoading(false);
            setDebateError("The council stopped before finishing.");
          },
        });
        debateUnsubRef.current = unsub;
      } catch (error: any) {
        console.error("Error starting Firestore debate:", error);
        setIsLoading(false);
        if (error?.code === "insufficient_debate_credits") {
          setBuyCreditsContext("debate");
          setDebateError("You're out of debate credits for this tier.");
        } else if (error?.code === "audio_limit" || error?.code === "free_limit") {
          setShowUsageLimitModal(error.code === "audio_limit" ? "audio" : "free");
          setDebateError(null);
        } else {
          setDebateError("Couldn't start the council. Check your connection and try again.");
        }
      }
    } else {
      // ── Plain chat mode — Firestore-backed streaming ────────────────────
      // Clean up any previous chat stream subscription
      if (chatStreamUnsubRef.current) {
        chatStreamUnsubRef.current();
        chatStreamUnsubRef.current = null;
      }

      try {
        const streamStarter = startChatStream(
          {
            messages: messagesForContext,
            model: selectedModel,
            uid: currentUser?.uid || null,
            email: currentUser?.email || null,
            generationMode,
          },
          {
            onText: (text) => {
              setMessages((prev) => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                updated[lastIndex] = {
                  ...updated[lastIndex],
                  text,
                };
                allMessagesRef.current = updated;
                return updated;
              });
            },
            onDone: (finalText, usedModel, streamMedia) => {
              const media = normalizeGeneratedMedia(streamMedia, finalText);
              setIsLoading(false);
              setMessages((prev) => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (lastIndex >= 0 && updated[lastIndex].sender === "ai") {
                  updated[lastIndex] = {
                    ...updated[lastIndex],
                    text: finalText,
                    model: usedModel,
                    media: media.length > 0 ? media : undefined,
                  };
                }
                allMessagesRef.current = updated;
                return updated;
              });
              // Clean up subscription
              if (chatStreamUnsubRef.current) {
                chatStreamUnsubRef.current();
                chatStreamUnsubRef.current = null;
              }

              // Image and video generations are ephemeral — don't save to history
              if (generationMode !== "text") return;

              // Save to history
              if (activeChatSessionId.current && currentUser) {
                const priorStored: StoredMessage[] = priorMessages
                  .filter((m) => !m.ephemeral)
                  .map((m) => {
                    const stored: StoredMessage = {
                      id: m.id,
                      text: m.text,
                      sender: m.sender,
                      timestamp:
                        m.timestamp instanceof Date
                          ? m.timestamp.getTime()
                          : Number(m.timestamp),
                    };
                    if (m.model) {
                      stored.model = m.model;
                    }
                    if (m.media && m.media.length > 0) {
                      stored.media = m.media;
                    }
                    return stored;
                  });
                const userTurn: StoredMessage = {
                  id: Date.now().toString(),
                  text: userMessage,
                  sender: "user",
                  timestamp: Date.now(),
                };
                const aiTurn: StoredMessage = {
                  id: (Date.now() + 1).toString(),
                  text: finalText,
                  sender: "ai",
                  timestamp: Date.now(),
                  model: usedModel,
                };
                if (media.length > 0) {
                  aiTurn.media = media;
                }
                const allStored = [...priorStored, userTurn, aiTurn];
                saveChatMessages(
                  currentUser.uid,
                  activeChatSessionId.current,
                  allStored,
                  usedModel,
                ).catch(console.error);
              }
            },
            onError: (error) => {
              console.error("Chat stream error:", error);
              setIsLoading(false);
              markLastAiMessageFailed(
                "The response stopped before it finished.",
                userMessage,
              );
              if (chatStreamUnsubRef.current) {
                chatStreamUnsubRef.current();
                chatStreamUnsubRef.current = null;
              }
            },
          },
        );
        const unsub = await streamStarter;
        chatStreamUnsubRef.current = unsub;
      } catch (error: any) {
        console.error("Error starting chat stream:", error);
        setIsLoading(false);
        if (error?.code === "insufficient_chat_credits") {
          setBuyCreditsContext("chat");
          markLastAiMessageFailed(
            "You're out of chat credits. Add credits, then try again.",
            userMessage,
          );
        } else {
          markLastAiMessageFailed(
            "Couldn't reach the model. Check your connection and try again.",
            userMessage,
          );
        }
        if (chatStreamUnsubRef.current) {
          chatStreamUnsubRef.current();
          chatStreamUnsubRef.current = null;
        }
      }
    }
  };

  const toggleExpertSection = (expert: string) => {
    setExpandedExperts((prev) => ({
      ...prev,
      [expert]: !prev[expert],
    }));
  };

  // ── Load a past chat session ──────────────────────────────────────────────
  const handleLoadChat = (session: ChatSession) => {
    setDebateError(null);
    leaveReopenedDebate();
    setIsLoading(false);
    // Clean up any active Firestore subscriptions
    if (debateUnsubRef.current) {
      debateUnsubRef.current();
      debateUnsubRef.current = null;
    }
    if (chatStreamUnsubRef.current) {
      chatStreamUnsubRef.current();
      chatStreamUnsubRef.current = null;
    }
    activeDebateId.current = null;
    setShownDebateId(null);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.stop();
      audioPlayerRef.current = null;
    }
    setCurrentSpeaker(null);
    setCurrentSpeakerLabel(null);
    setCurrentPhase(null);
    setCompletedAudioTurns([]);

    if (!session.messages || session.messages.length === 0) {
      // Session exists but has no messages (e.g. save didn't complete) — nothing to show
      setMessages([]);
      allMessagesRef.current = [];
      setCouncilMode(false);
      activeChatSessionId.current = session.id;
      activeDebateSessionId.current = null;
      return;
    }
    // Convert stored messages back to the local Message shape
    const restored: Message[] = session.messages.map((m) => ({
      id: m.id,
      text: m.text,
      sender: m.sender as "user" | "ai",
      timestamp: new Date(
        typeof m.timestamp === "number" ? m.timestamp : Number(m.timestamp),
      ),
      model: m.model,
      media: m.media,
    }));
    setCouncilMode(false);
    setDebateStarted(false);
    setCouncilProgress([]);
    setChaosProgress([]);
    setCouncilFinal(null);
    showVerdict(null, null);
    showSources([]);
    setFacilitatorDone(false);
    setDebateTopic(null);
    activeChatSessionId.current = session.id;
    activeDebateSessionId.current = null;
    // Set messages last so councilMode=false is already applied
    setMessages(restored);
    allMessagesRef.current = restored;
  };

  // ── Load a past debate session ────────────────────────────────────────────
  // The browser saves a debate into history when it finishes, so leaving early
  // skips that even though the backend finishes the debate. Read the real thing.
  const resumeSavedDebate = (session: DebateSession, debateId: string) => {
    const stop = () => {
      debateUnsubRef.current?.();
      debateUnsubRef.current = null;
      setIsLoading(false);
    };
    // A finished audio debate reopens as one, ready to replay. Until a debate
    // has finished there's no complete recording, so it reads as text.
    const showFinished = (audio: boolean | undefined) => {
      if (audio !== true) {
        setReopenedFormat("text");
        return;
      }
      const turns = finishedAudioTurns(session.experts);
      savedAudioRef.current = { debateId, turns };
      setCompletedAudioTurns(turns);
      setReopenedFormat("audio");
    };
    const alreadySaved = !!session.councilFinal;
    if (!alreadySaved) setIsLoading(true);
    debateUnsubRef.current = onSavedDebateSnapshot(
      debateId,
      (debate) => {
        if (alreadySaved) {
          stop();
          showFinished(debate.audio);
          return;
        }
        setReopenedFormat("text");
        const steps = debate.steps || [];
        const chaosSteps = debate.chaosSteps || [];
        const final = debate.finalAggregate || null;
        const verdict = parseVerdict(debate.verdict);
        const answerFormat = parseAnswerFormat(debate.answerFormat);
        setCouncilProgress(steps);
        setChaosProgress(chaosSteps);
        setCouncilFinal(final);
        if (
          JSON.stringify(verdict) !== JSON.stringify(councilVerdictRef.current) ||
          JSON.stringify(answerFormat) !== JSON.stringify(debateAnswerFormatRef.current)
        ) {
          showVerdict(verdict, answerFormat);
        }
        const sources = parseSources(debate.sources);
        showSources(sources);

        const state = debateResumeState(debate, Date.now());
        if (state === "live") return;
        stop();
        if (state === "failed") {
          setDebateError("The council stopped before finishing.");
          return;
        }
        setFacilitatorDone(true);
        showFinished(debate.audio);
        if (currentUser) {
          saveDebateSession(
            currentUser.uid,
            session.id,
            steps,
            chaosSteps,
            final,
            { verdict, answerFormat, sources },
            { keepPosition: true },
          ).catch(console.error);
        }
      },
      () => {
        stop();
        setReopenedFormat("text");
        if (!alreadySaved) setDebateError("This debate couldn't be loaded.");
      },
    );
  };

  const handleLoadDebate = (session: DebateSession) => {
    // Clean up any active Firestore subscriptions
    if (debateUnsubRef.current) {
      debateUnsubRef.current();
      debateUnsubRef.current = null;
    }
    if (chatStreamUnsubRef.current) {
      chatStreamUnsubRef.current();
      chatStreamUnsubRef.current = null;
    }
    activeDebateId.current = null;
    setShownDebateId(null);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.stop();
      audioPlayerRef.current = null;
    }
    setCurrentSpeaker(null);
    setCurrentSpeakerLabel(null);
    setCurrentPhase(null);
    setCompletedAudioTurns([]);

    setCouncilMode(true);
    setDebateStarted(true);
    setDebateTopic(session.topic);
    setSelectedExperts(session.experts);
    setPlan(session.plan as "free" | "plus" | "pro");
    setDebateMode(session.debateMode || "multi");
    setCouncilProgress(session.councilProgress);
    setChaosProgress(session.chaosProgress);
    setCouncilFinal(session.councilFinal);
    showVerdict(parseVerdict(session.verdict), parseAnswerFormat(session.answerFormat));
    showSources(parseSources(session.sources));
    setFacilitatorDone(!!session.councilFinal);
    setMessages([]);
    allMessagesRef.current = [];
    // Start with all panels collapsed — user can expand what they want.
    // The facilitator final answer is always visible (not gated by expandedExperts).
    setExpandedExperts({});
    activeDebateSessionId.current = session.id;
    activeChatSessionId.current = null;
    feedShareRef.current = null;
    setShownDebateId(session.debateId ?? null);
    setDebateError(null);
    setIsLoading(false);
    leaveReopenedDebate();
    // The debate doc is what knows whether it was an audio debate.
    if (session.debateId && session.debateMode !== "single") {
      setReopenedFormat("checking");
      resumeSavedDebate(session, session.debateId);
      return;
    }
    setReopenedFormat("text");
    if (session.councilFinal) return;
    if (session.debateId) resumeSavedDebate(session, session.debateId);
    else setDebateError("This debate couldn't be loaded.");
  };

  return (
    <div className={`app-frame${councilMode && (debateStarted || isLoading) ? " is-page-scroll" : ""}`}>
      <a href="#main-content" className="skip-link">Skip to content</a>
      {/* History Sidebar — only for logged-in users */}
      {currentUser && (
        <HistorySidebar
          uid={currentUser.uid}
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          onLoadChat={handleLoadChat}
          onLoadDebate={handleLoadDebate}
        />
      )}

      {/* Guest sign-in prompt when they try to open history */}
      <Dialog open={!currentUser && showHistory} onClose={() => setShowHistory(false)} title="Chat history">
        <p className="text-sm text-muted leading-7">Sign in to save your chats and debates.</p>
        <button type="button" className="button-primary w-full" onClick={() => { setShowHistory(false); handleSignIn(); }}>Sign in with Google</button>
        <button type="button" className="button-secondary w-full" onClick={() => setShowHistory(false)}>Close</button>
      </Dialog>

      {/* Header */}
      <ChatHeader
        currentUser={currentUser}
        isGuest={isGuest}
        onLogout={handleLogout}
        onSignIn={handleSignIn}
        councilMode={councilMode}
        onToggleCouncilMode={() => {
          setCouncilMode((v) => !v);
          activeChatSessionId.current = null;
          activeDebateSessionId.current = null;
        }}
        selectedModel={selectedModel}
        onSelectModel={setSelectedModel}
        modelOptions={modelOptions}
        messages={messages}
        onNewChat={handleNewChat}
        credits={credits}
        onBuyCredits={() =>
          setBuyCreditsContext(councilMode ? "debate" : "chat")
        }
        onOpenHistory={() => setShowHistory(true)}
        onOpenVideo={() => navigate("/video")}
        usage={usage}
        todayCost={todayCost}
        isPrivileged={isPrivileged}
      />

      <div className={`workspace-body ${!councilMode ? "is-chat" : "is-council"}`}>
      {!councilMode && (
        <aside className="bubble-rail chat-rail" aria-label="Chat tools">
          <div className="rail-heading"><span className="rail-icon"><Bot size={21} /></span><h2>Direct Chat</h2></div>
          <div className="rail-actions">
            <button type="button" className="rail-action is-active" onClick={handleNewChat}><MessageCircle size={18} /><span>New conversation</span><span aria-hidden="true">+</span></button>
            <button type="button" className="rail-action" onClick={() => setShowModelBrowser(true)}><Bot size={18} /><span>Explore models</span></button>
            <button type="button" className="rail-action" onClick={() => setShowHistory(true)}><Clock size={18} /><span>Conversation history</span></button>
          </div>
          <div className="rail-note"><span className="eyebrow">CURRENT MODEL</span><h3>{modelOptions.find(m => m.value === selectedModel)?.label || selectedModel.split("/").pop()}</h3><button type="button" onClick={() => setShowModelBrowser(true)}>Change model <span aria-hidden="true">↗</span></button></div>
          <div className="rail-bottom"><span className="rail-status-dot" />{currentUser ? "History saved to your account" : "Guest session · history isn’t saved"}</div>
        </aside>
      )}
      <div className="workspace-content">
      {/* Chat Messages */}
      <main
        id="main-content"
        onScrollCapture={handleMessagesScroll}
        className={`flex-1 w-full min-h-0 ${
          councilMode && !debateStarted && !isLoading
            ? "overflow-hidden flex flex-col"
            : councilMode
              ? "overflow-x-clip space-y-6 px-4 sm:px-6 lg:px-8 py-4 max-w-7xl mx-auto"
              : "overflow-hidden flex flex-col"
        }`}
      >
        {councilMode && debateStarted && <h1 className="sr-only">Your council’s perspective</h1>}
        {!councilMode && messages.length > 0 && <h1 className="sr-only">Direct chat</h1>}
          <div className={councilMode ? "contents" : "chat-conversation relative"}>
            <div ref={chatScrollRef} className={councilMode ? "contents" : "chat-messages"} role={!councilMode && messages.length > 0 ? "log" : undefined} aria-label={!councilMode && messages.length > 0 ? "Conversation" : undefined}>
        {messages.length === 0 && !isLoading && !councilMode && (
          <div className="chat-empty-state">
            <h1 className="sr-only">Direct Chat</h1>
            <ModelBrowser
              models={generationFilteredModels}
              loading={modelsLoading}
              selectedModel={selectedModel}
              onSelectModel={setSelectedModel}
              error={modelsError}
              onRetry={refreshModels}
            />
          </div>
        )}

        {/* ── Debate Wizard (stepped flow before debate starts) ──────── */}
        {councilMode && (
          <DebateWizard
            selectedExperts={selectedExperts}
            onToggleExpert={handleExpertToggle}
            onSetExperts={setSelectedExperts}
            onClearExperts={() => setSelectedExperts([])}
            expertOptions={expertOptions}
            onStartDebate={handleStartDebate}
            isLoading={isLoading}
            hasDebateContent={debateStarted}
            plan={plan}
            onSetPlan={setPlan}
            concise={concise}
            onSetConcise={setConcise}
            audioMode={audioMode}
            onSetAudioMode={setAudioMode}
            credits={credits}
            isGuest={isGuest}
            onSignIn={handleSignIn}
            usage={usage}
            debateMode={debateMode}
            onSetDebateMode={setDebateMode}
            isPrivileged={isPrivileged}
          />
        )}

        {/* Council mode progress display */}
        {councilMode && debateStarted && reopenedFormat === "checking" && (
          <div role="status" className="py-12 text-center text-sm text-muted">
            Loading debate…
          </div>
        )}

        {councilMode &&
          debateStarted &&
          reopenedFormat !== "checking" &&
          (debateMode === "single" || !showAudioDebate) && (
            <>
              <DebateViewSwitch value={debateView} onChange={setDebateView} />
              {debateView === "thread" ? (
                <DebateThreadView
                  debateTopic={debateTopic}
                  debateImages={debateImagesRef.current}
                  councilProgress={councilProgress}
                  chaosProgress={chaosProgress}
                  councilFinal={councilFinal}
                  facilitatorDone={facilitatorDone}
                  isLoading={isLoading}
                  selectedExperts={selectedExperts}
                  debateMode={debateMode}
                  verdict={councilVerdict}
                  answerFormat={debateAnswerFormat}
                  sources={debateSources}
                />
              ) : (
                <DebateProgress
                  debateTopic={debateTopic}
                  debateImages={debateImagesRef.current}
                  councilProgress={councilProgress}
                  chaosProgress={chaosProgress}
                  councilFinal={councilFinal}
                  facilitatorDone={facilitatorDone}
                  isLoading={isLoading}
                  selectedExperts={selectedExperts}
                  expandedExperts={expandedExperts}
                  onToggleExpert={toggleExpertSection}
                  debateMode={debateMode}
                  verdict={councilVerdict}
                  sources={debateSources}
                />
              )}
            </>
          )}

        {/* Audio debate progress display */}
        {councilMode &&
          debateStarted &&
          reopenedFormat !== "checking" &&
          showAudioDebate &&
          debateMode === "multi" && (
            <AudioDebateView
              debateTopic={debateTopic}
              debateImages={debateImagesRef.current}
              councilProgress={councilProgress}
              chaosProgress={chaosProgress}
              councilFinal={councilFinal}
              facilitatorDone={facilitatorDone}
              audioFinished={facilitatorDone && audioPlaybackState === "idle"}
              isLoading={isLoading}
              selectedExperts={selectedExperts}
              expandedExperts={expandedExperts}
              onToggleExpert={toggleExpertSection}
              currentSpeaker={currentSpeaker}
              currentSpeakerLabel={currentSpeakerLabel}
              currentPhase={currentPhase}
              completedTurns={completedAudioTurns}
              playbackState={audioPlaybackState}
              onPause={handleAudioPause}
              onResume={handleAudioResume}
              onReplay={handleAudioReplay}
              verdict={councilVerdict}
              sources={debateSources}
            />
          )}

        {!councilMode &&
          messages.map((message, index) => (
            <div
              key={message.id}
              className={`motion-rise flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-4 py-3 overflow-hidden ${
                  message.sender === "user"
                    ? "bg-action text-on-solid rounded-lg rounded-tr-none shadow-xs px-5 py-3.5"
                    : "bg-subtle border border-line/90 rounded-lg rounded-tl-none shadow-xs px-5 py-3.5 text-content"
                }`}
              >
                <div className="flex items-start space-x-2">
                  {message.sender === "ai" && (
                    <div className="mt-0.5 flex-shrink-0">
                      <div className="w-7 h-7 rounded-lg bg-pressed/80 text-accent flex items-center justify-center flex-shrink-0"><Bot size={15} /></div>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    {/* User-attached images */}
                    {message.images && message.images.length > 0 && (
                      <div className="flex gap-2 mb-2">
                        {message.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Attached ${idx + 1}`}
                            className={`w-24 h-24 object-cover rounded-xl border ${
                              message.sender === "user"
                                ? "border-accent-line/40"
                                : "border-line"
                            }`}
                          />
                        ))}
                      </div>
                    )}
                    {message.sender === "ai" &&
                      message.media &&
                      message.media.length > 0 && (
                        <div className="space-y-2 mb-2">
                          {message.media.map((item, idx) => {
                            const mediaKey = `${item.type}|${item.url}|${idx}`;
                            const failed = !!failedMediaUrls[item.url];
                            return (
                              <div
                                key={mediaKey}
                                className="rounded-xl border border-line overflow-hidden bg-subtle"
                              >
                                <div className="w-full bg-overlay/5">
                                  {item.type === "image" ? (
                                    failed ? (
                                      <div className="h-44 w-full flex items-center justify-center text-xs text-muted">
                                        Could not load image preview.
                                      </div>
                                    ) : (
                                      <img
                                        src={item.url}
                                        alt="Generated visual"
                                        className="w-full max-h-80 object-cover cursor-zoom-in"
                                        onClick={() =>
                                          setFullscreenImageUrl(item.url)
                                        }
                                        onError={() =>
                                          markMediaLoadError(item.url)
                                        }
                                      />
                                    )
                                  ) : failed ? (
                                    <div className="h-44 w-full flex items-center justify-center text-xs text-muted">
                                      Could not load video preview.
                                    </div>
                                  ) : (
                                    <video
                                      controls
                                      preload="metadata"
                                      poster={item.posterUrl}
                                      className="w-full max-h-80 bg-media"
                                      onError={() =>
                                        markMediaLoadError(item.url)
                                      }
                                    >
                                      <source
                                        src={item.url}
                                        type={item.mimeType}
                                      />
                                      Your browser does not support inline video
                                      playback.
                                    </video>
                                  )}
                                </div>
                                <div className="flex items-center justify-end gap-2 p-2 bg-surface border-t border-line">
                                  <button
                                    type="button"
                                    onClick={() => handleMediaDownload(item)}
                                    disabled={downloadingMediaUrl === item.url}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-accent-line bg-accent-soft text-accent hover:bg-accent-soft disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    {downloadingMediaUrl === item.url ? (
                                      <Loader2
                                        size={12}
                                        className="animate-spin"
                                      />
                                    ) : (
                                      <Download size={12} />
                                    )}
                                    Download
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    {message.sender === "ai" && !message.text && !message.error && !message.media?.length ? (
                      <div className="flex items-center gap-1 h-6" role="status" aria-label="Waiting for response">
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                      </div>
                    ) : message.text ? (
                      <StreamingMarkdown
                        text={message.text}
                        streaming={message.sender === "ai" && isLoading && index === messages.length - 1}
                        components={markdownComponents}
                        className="chat-markdown min-w-0 max-w-full whitespace-pre-wrap break-words overflow-x-hidden"
                      />
                    ) : null}
                    {message.error && (
                      <div className="message-error motion-fade" role="alert">
                        <span>{message.error}</span>
                        {message.retryText && (
                          <button
                            type="button"
                            onClick={() => handleRetryMessage(message.id)}
                            disabled={isLoading}
                            className="disabled:opacity-50 disabled:no-underline focus-visible:outline-offset-2"
                          >
                            Try again
                          </button>
                        )}
                      </div>
                    )}
                    {message.sender === "ai" && message.text && !(isLoading && index === messages.length - 1) && (
                      <div className="message-footer motion-fade">
                        {message.model && message.model !== "openrouter/auto" ? (
                          <span className="font-mono text-xs text-muted truncate">{message.model}</span>
                        ) : <span />}
                        <CopyButton label="Copy response" getText={() => message.text} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

        {councilMode && debateError && (
          <div className="message-error motion-fade max-w-3xl mx-auto" role="alert">
            <span>{debateError} Your topic and panel are saved; start a new debate to try again.</span>
          </div>
        )}

        {councilMode && audioReplayError && (
          <div className="message-error motion-fade max-w-3xl mx-auto" role="alert">
            <span>{audioReplayError}</span>
          </div>
        )}

        <div ref={messagesEndRef} />
            </div>
            {!councilMode && showJumpToLatest && (
              <button type="button" className="jump-latest focus-visible:outline-offset-2" onClick={jumpToLatest}>
                <ChevronDown size={14} aria-hidden="true" />
                Jump to latest
              </button>
            )}
          </div>
      </main>

      {/* Input Area (Footer) */}
      {councilMode &&
      !debateStarted /* No footer when wizard is showing — it has its own navigation */ ? null : (
        <footer
          className={`flex-shrink-0 bg-surface border-t border-line px-4 pt-2 sm:pt-4 w-full ${!councilMode ? "chat-composer" : "debate-footer"}`}
          style={{
            paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0.5rem))",
          }}
        >
          {councilMode && debateStarted ? (
            /* ── Debate mode footer (running / completed) ───────────────── */
            <div className="max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto">
              {isLoading ? (
                <div role="status" className="flex items-center justify-center gap-3 py-3 px-4 rounded-lg bg-subtle border border-line text-muted text-sm">
                  <div className="flex items-center gap-1">
                    <span className="typing-dot" aria-hidden="true" />
                    <span className="typing-dot" aria-hidden="true" />
                    <span className="typing-dot" aria-hidden="true" />
                  </div>
                  <span>The council is working through your question…</span>
                </div>
              ) : facilitatorDone &&
                !reopenedFormat &&
                audioMode &&
                audioPlaybackState !== "idle" ? (
                /* Audio is still playing after text generation finished */
                <div className="flex items-center justify-center gap-3 py-3 px-4 rounded-lg bg-subtle border border-line text-muted text-sm">
                  <div className="flex items-end gap-[3px] h-4">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="w-[3px] bg-success-solid rounded-full animate-sound-wave"
                        style={{
                          animationDelay: `${i * 0.12}s`,
                          height: "100%",
                        }}
                      />
                    ))}
                  </div>
                  <span>
                    Audio playing… debate will complete when playback finishes
                  </span>
                </div>
              ) : facilitatorDone ? (
                <div className="flex items-center justify-between gap-3">
                  <DebateShareMenu
                    pdf={{ debateTopic: debateTopic ?? "", councilProgress, chaosProgress, councilFinal, selectedExperts, debateMode, sources: debateSources }}
                    shareLink={shareLink}
                    feedPost={feedPost}
                    debateId={shownDebateId && councilFinal ? shownDebateId : null}
                    signedIn={!!currentUser}
                  />
                  <button type="button" onClick={startNewDebate} className="button-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2">
                    <Plus size={16} aria-hidden="true" />
                    New debate
                  </button>
                </div>
              ) : debateError ? (
                <div className="flex justify-end">
                  <button type="button" onClick={startNewDebate} className="button-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2">
                    <Plus size={16} aria-hidden="true" />
                    New debate
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            /* ── Normal chat footer ─────────────────────────────────────── */
            <form onSubmit={handleSubmit} className="max-w-3xl w-full mx-auto">
              {/* Active model indicator */}
              {(() => {
                const currentModel = modelOptions.find(
                  (m) => m.value === selectedModel,
                );
                const displayLabel =
                  selectedModel === "openrouter/auto"
                    ? "Auto"
                    : (currentModel?.label ??
                      selectedModel.split("/").pop()?.replace(/:.*$/, "") ??
                      selectedModel);
                return (
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-1.5 text-xs text-muted">
                      <Bot size={12} className="text-muted" />
                      <span className="font-medium text-muted">
                        {displayLabel}
                      </span>
                      {currentModel?.vision && (
                        <Camera size={10} className="text-accent" />
                      )}
                      {currentModel?.imageGeneration && (
                        <ImageIcon size={10} className="text-success" />
                      )}
                      {currentModel?.videoGeneration && (
                        <Film size={10} className="text-favorite" />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowModelBrowser((v) => !v)}
                      className="flex items-center gap-1 text-xs text-accent hover:text-accent font-semibold transition-colors"
                    >
                      Change
                    </button>
                  </div>
                );
              })()}
              {/* Model browser modal */}
              <Dialog open={showModelBrowser} onClose={() => setShowModelBrowser(false)} title="Choose a model" wide>
                <ModelBrowser models={generationFilteredModels} loading={modelsLoading} selectedModel={selectedModel} onSelectModel={m => { setSelectedModel(m); setShowModelBrowser(false); }} showHeader={false} error={modelsError} onRetry={refreshModels} />
              </Dialog>
              {/* Image preview strip */}
              {chatImages.length > 0 && (
                <div className="flex gap-2 mb-2 px-1">
                  {chatImages.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={img}
                        alt={`Attached ${idx + 1}`}
                        className="w-14 h-14 object-cover rounded-xl border border-line shadow-sm"
                      />
                      <button
                        type="button"
                        aria-label={`Remove attachment ${idx + 1}`}
                        onClick={() => removeChatImage(idx)}
                        className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-danger-solid text-on-solid rounded-full flex items-center justify-center text-xs shadow-sm hover:bg-danger-solid"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="seg-control mb-2 ml-1" role="group" aria-label="Generation mode">
                <SlidingIndicator selector='[aria-pressed="true"]' activeKey={generationMode} className="seg-indicator-ink" />
                {(
                  [
                    ["text", "Text", Bot],
                    ["image", "Image", ImageIcon],
                  ] as [GenerationMode, string, React.ElementType][]
                ).map(([modeValue, label, Icon]) => {
                  const active = generationMode === modeValue;
                  return (
                    <button
                      key={modeValue}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setGenerationMode(modeValue)}
                      disabled={isLoading}
                      className={`seg-item ${active ? "text-on-solid" : "text-muted hover:text-content"}`}
                    >
                      <Icon size={12} aria-hidden="true" />
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center space-x-2">
                {/* Hidden file input */}
                <input
                  ref={chatFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleChatImageSelect}
                />
                {/* Image upload button */}
                <button
                  type="button"
                  disabled={
                    chatImages.length >= MAX_CHAT_IMAGES ||
                    compressingChatImages ||
                    isLoading ||
                    generationMode !== "text"
                  }
                  onClick={() => chatFileInputRef.current?.click()}
                  aria-label={
                    generationMode !== "text"
                      ? "Attach photo (unavailable in generation modes)"
                      : chatImages.length >= MAX_CHAT_IMAGES
                        ? `Attach photo (${MAX_CHAT_IMAGES} photos max)`
                        : "Attach photo"
                  }
                  className={`p-3 rounded-md transition-colors flex-shrink-0 ${
                    chatImages.length >= MAX_CHAT_IMAGES ||
                    isLoading ||
                    generationMode !== "text"
                      ? "text-muted cursor-not-allowed"
                      : compressingChatImages
                        ? "text-accent animate-pulse"
                        : "text-muted hover:text-muted hover:bg-raised"
                  }`}
                  title={
                    generationMode !== "text"
                      ? "Attachment disabled in generation modes"
                      : chatImages.length >= MAX_CHAT_IMAGES
                        ? `${MAX_CHAT_IMAGES} photos max`
                        : "Attach photo"
                  }
                >
                  {compressingChatImages ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <ImagePlus size={20} />
                  )}
                </button>
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={inputText}
                    aria-label={generationMode === "image" ? "Image prompt" : "Message"}
                    onPaste={handleChatPaste}
                    onChange={(e) => { setInputText(e.target.value); e.target.style.height = "auto"; e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`; }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && window.matchMedia("(pointer: fine)").matches) {
                        e.preventDefault();
                        handleSubmit(e as any);
                      }
                    }}
                    placeholder={
                      isLoading
                        ? "Write your next message…"
                        : generationMode === "image"
                          ? "Describe the image you want generated..."
                          : generationMode === "video"
                            ? "Describe the video you want generated..."
                            : "Type your message..."
                    }
                    className="w-full border-2 rounded-lg px-4 py-3 pr-12 resize-none focus:outline-none focus:ring-2 focus:ring-accent-line/20 focus:border-accent-line text-base sm:text-sm transition-colors duration-150 border-control bg-surface hover:border-control"
                    rows={1}
                    style={{ minHeight: "48px", maxHeight: "120px" }}
                  />
                </div>
                <button
                  type="submit"
                  aria-label={isLoading ? "Wait for the current response to finish" : "Send message"}
                  title={isLoading ? "Wait for the current response to finish" : undefined}
                  disabled={!inputText.trim() || isLoading}
                  className={`p-3 rounded-md transition-[background-color,color,transform] duration-150 active:translate-y-px focus-visible:outline-offset-2 ${inputText.trim() && !isLoading ? "bg-action text-on-solid hover:bg-action-hover shadow-xs" : "bg-pressed text-muted cursor-not-allowed"}`}
                >
                  <Send size={20} />
                </button>
              </div>
            </form>
          )}
        </footer>
      )}
      </div>
      </div>

      {/* Debate Credits Modal */}
      {currentUser && (
        <BuyCreditsModal
          isOpen={buyCreditsContext === "debate"}
          onClose={() => setBuyCreditsContext(null)}
          plusCredits={credits.plusCredits}
          proCredits={credits.proCredits}
          uid={currentUser.uid}
        />
      )}

      {/* Chat Credits Modal */}
      {currentUser && (
        <BuyChatCreditsModal
          isOpen={buyCreditsContext === "chat"}
          onClose={() => setBuyCreditsContext(null)}
          chatCredits={credits.chatCredits ?? 0}
          uid={currentUser.uid}
        />
      )}
      {/* Guest prompt when they try to use paid tiers */}
      <Dialog open={!currentUser && !!buyCreditsContext} onClose={() => setBuyCreditsContext(null)} title="Sign in required">
        <p className="text-sm text-muted leading-7">Sign in to add credits and choose paid models. You can keep using free models as a guest.</p>
        <button type="button" className="button-primary w-full" onClick={() => { setBuyCreditsContext(null); handleSignIn(); }}>Sign in with Google</button>
        <button type="button" className="button-secondary w-full" onClick={() => setBuyCreditsContext(null)}>Close</button>
      </Dialog>

      {/* Weekly usage limit modal */}
      <Dialog open={!!showUsageLimitModal} onClose={() => setShowUsageLimitModal(null)} title="Weekly limit reached">
        <div className="w-full text-center">
            {showUsageLimitModal === "audio" ? (
              <>
                <p className="text-sm text-muted mb-1">
                  You've used all{" "}
                  <span className="font-semibold text-content">
                    {WEEKLY_AUDIO_DEBATE_LIMIT} audio debates
                  </span>{" "}
                  allowed this week.
                </p>
                <p className="text-xs text-muted mb-6">
                  Audio debate limit resets every Monday at midnight UTC.
                  Upgrade to Plus or Pro for more.
                </p>
              </>
            ) : showUsageLimitModal === "guest" ? (
              <>
                <p className="text-sm text-muted mb-1">
                  You've used all{" "}
                  <span className="font-semibold text-content">
                    {GUEST_WEEKLY_FREE_DEBATE_LIMIT} free debates
                  </span>{" "}
                  allowed this week.
                </p>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="h-2 w-32 bg-danger-soft rounded-full overflow-hidden">
                    <div className="h-full w-full bg-danger-solid rounded-full" />
                  </div>
                  <span className="text-xs font-semibold text-danger">
                    {GUEST_WEEKLY_FREE_DEBATE_LIMIT}/
                    {GUEST_WEEKLY_FREE_DEBATE_LIMIT}
                  </span>
                </div>
                <p className="text-xs text-muted mb-6">
                  Sign in for {WEEKLY_FREE_DEBATE_LIMIT} free debates/week,
                  audio mode, and saved history!
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-muted mb-1">
                  You've used all{" "}
                  <span className="font-semibold text-content">
                    {WEEKLY_FREE_DEBATE_LIMIT} free debates
                  </span>{" "}
                  allowed this week.
                </p>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="h-2 w-32 bg-danger-soft rounded-full overflow-hidden">
                    <div className="h-full w-full bg-danger-solid rounded-full" />
                  </div>
                  <span className="text-xs font-semibold text-danger">
                    {WEEKLY_FREE_DEBATE_LIMIT}/{WEEKLY_FREE_DEBATE_LIMIT}
                  </span>
                </div>
                <p className="text-xs text-muted mb-6">
                  Free debate limit resets every Monday at midnight UTC. Upgrade
                  to Plus or Pro for unlimited debates!
                </p>
              </>
            )}

            <div className="flex flex-col gap-2">
              {showUsageLimitModal === "guest" ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowUsageLimitModal(null);
                    handleSignIn();
                  }}
                  className="px-5 py-2.5 bg-action hover:bg-action-hover text-on-solid text-sm font-semibold rounded-md transition-colors shadow-sm"
                >
                  Sign in for More Debates
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setShowUsageLimitModal(null);
                    setBuyCreditsContext("debate");
                  }}
                  className="px-5 py-2.5 bg-action hover:bg-action-hover text-on-solid text-sm font-semibold rounded-md transition-colors shadow-sm"
                >
                  Upgrade for More
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowUsageLimitModal(null)}
                className="text-sm text-muted hover:text-muted"
              >
                Dismiss
              </button>
            </div>
        </div>
      </Dialog>

      <Dialog open={!!fullscreenImageUrl} onClose={() => setFullscreenImageUrl(null)} title="Generated image" wide>
        {fullscreenImageUrl && <img src={fullscreenImageUrl} alt="Fullscreen generated visual" className="max-h-[75dvh] w-full object-contain rounded-xl" />}
      </Dialog>

    </div>
  );
};

export default Chat;
